import { type ReactNode, createContext, useContext } from 'react';
import type { Map, OrderedMap } from '@gonfalon/immutable-shim/immutableShim';
import { List } from '@gonfalon/immutable-shim/immutableShim';
import invariant from 'tiny-invariant';

import type { AccessToken } from '#utils/accessTokenUtils';
import type { Account } from '#utils/accounts/records/Account';
import type { AiConfig } from '#utils/aiConfigUtils';
import { createAiConfig } from '#utils/aiConfigUtils';
import type { ApprovalRequest } from '#utils/approvals/records/ApprovalRequest';
import { ExperimentApprovalResource } from '#utils/approvals/types';
import type { ApprovalRequestResourceKind } from '#utils/approvals/types';
import type { ValidationEntity } from '#utils/confirmationUtils';
import { aiConfigValiation, experimentValidation, flagValidation, segmentValidation } from '#utils/confirmationUtils';
import type { ApprovalSettings } from '#utils/environments/records/ApprovalSettings';
import { createApprovalRequestSettings } from '#utils/environments/records/ApprovalSettings';
import type { Environment } from '#utils/environments/records/Environment';
import { createEnvironment } from '#utils/environments/records/Environment';
import type { Flag } from '#utils/flags/records/Flag';
import type { IntegrationsManifestServed } from '#utils/goaltenderUtils';
import type { Member } from '#utils/members/records/Member';
import type { NestedWorkflow } from '#utils/pendingChanges/records/NestedWorkflow';
import type { Project } from '#utils/projects/records/Project';
import type { Segment } from '#utils/segment/records/Segment';

type ApprovalRequestDetailsDataCommonProps = {
  approvalRequest: ApprovalRequest;
  project: Project;
  account: Account;
  members: Map<string, Member>;
  serviceTokens?: OrderedMap<string, AccessToken>;
  profile: Member;
  nestedWorkflow?: NestedWorkflow;
  integrationManifest?: IntegrationsManifestServed;
};

export type ApprovalRequestDetailsDataContextValue = Readonly<
  ApprovalRequestDetailsDataCommonProps &
    (
      | {
          resourceKind: 'FlagConfiguration';
          environment: Environment;
          impactedEnvironments?: undefined;
          environmentSettings?: undefined;
          resource: Flag;
          approvalSettings: ApprovalSettings;
          resourceValidation: (resource: ValidationEntity) => {
            label: ReactNode;
            placeholder: string;
            onValidate: (input: string) => boolean;
          };
        }
      | {
          resourceKind: 'Flag';
          environment?: undefined;
          impactedEnvironments: List<Environment>;
          environmentSettings: Environment;
          resource: Flag;
          approvalSettings: ApprovalSettings;
          resourceValidation: (resource: ValidationEntity) => {
            label: ReactNode;
            placeholder: string;
            onValidate: (input: string) => boolean;
          };
        }
      | {
          resourceKind: 'Segment';
          environment: Environment;
          impactedEnvironments?: undefined;
          environmentSettings?: undefined;
          resource: Segment;
          // approvalSettings: ResourceApprovalSettings; TODO: resourceApprovalSettings aren't being returned in API response
          approvalSettings: ApprovalSettings;
          resourceValidation: (resource: ValidationEntity) => {
            label: ReactNode;
            placeholder: string;
            onValidate: (input: string) => boolean;
          };
        }
      | {
          resourceKind: 'AiConfig';
          environment?: Environment;
          impactedEnvironments: List<Environment>;
          environmentSettings: Environment;
          resource: AiConfig;
          approvalSettings: ApprovalSettings;
          resourceValidation: (resource: ValidationEntity) => {
            label: ReactNode;
            placeholder: string;
            onValidate: (input: string) => boolean;
          };
        }
      | {
          resourceKind: 'Experiment';
          environment: Environment;
          impactedEnvironments?: undefined;
          environmentSettings?: undefined;
          resource: ExperimentApprovalResource;
          approvalSettings: ApprovalSettings;
          resourceValidation: (resource: ValidationEntity) => {
            label: ReactNode;
            placeholder: string;
            onValidate: (input: string) => boolean;
          };
        }
    )
>;

export const ApprovalRequestContext = createContext<ApprovalRequestDetailsDataContextValue>(
  {} as ApprovalRequestDetailsDataContextValue,
);

export function useApprovalRequestContext() {
  return useContext(ApprovalRequestContext);
}

export function getApprovalRequestContextPropsForResourceKind(props: {
  resourceKind: 'FlagConfiguration';
  approvalRequest: ApprovalRequest;
  currentEnvironment: Environment;
  projectEnvironments: List<Environment>;
  flag?: Flag;
}): {
  resourceKind: 'FlagConfiguration';
  environment: Environment;
  approvalSettings: ApprovalSettings;
  resource: Flag;
  resourceValidation: (resource: ValidationEntity) => {
    label: ReactNode;
    placeholder: string;
    onValidate: (input: string) => boolean;
  };
};
export function getApprovalRequestContextPropsForResourceKind(props: {
  resourceKind: 'Flag';
  approvalRequest: ApprovalRequest;
  currentEnvironment: Environment;
  projectEnvironments: List<Environment>;
  flag?: Flag;
}): {
  resourceKind: 'Flag';
  impactedEnvironments: List<Environment>;
  approvalSettings: ApprovalSettings;
  environmentSettings: Environment;
  resource: Flag;
  resourceValidation: (resource: ValidationEntity) => {
    label: ReactNode;
    placeholder: string;
    onValidate: (input: string) => boolean;
  };
};
export function getApprovalRequestContextPropsForResourceKind(props: {
  resourceKind: 'Segment';
  approvalRequest: ApprovalRequest;
  currentEnvironment: Environment;
  projectEnvironments: List<Environment>;
}): {
  resourceKind: 'Segment';
  environment: Environment;
  approvalSettings: ApprovalSettings;
  resource: Segment;
  resourceValidation: (resource: ValidationEntity) => {
    label: ReactNode;
    placeholder: string;
    onValidate: (input: string) => boolean;
  };
};
export function getApprovalRequestContextPropsForResourceKind(props: {
  resourceKind: ApprovalRequestResourceKind;
  approvalRequest: ApprovalRequest;
  currentEnvironment: Environment;
  projectEnvironments: List<Environment>;
  flag?: Flag;
}):
  | {
      resourceKind: 'FlagConfiguration';
      environment: Environment;
      approvalSettings: ApprovalSettings;
      resource: Flag;
      resourceValidation: (resource: ValidationEntity) => {
        label: ReactNode;
        placeholder: string;
        onValidate: (input: string) => boolean;
      };
    }
  | {
      resourceKind: 'Flag';
      impactedEnvironments: List<Environment>;
      approvalSettings: ApprovalSettings;
      environmentSettings: Environment;
      resource: Flag;
      resourceValidation: (resource: ValidationEntity) => {
        label: ReactNode;
        placeholder: string;
        onValidate: (input: string) => boolean;
      };
    }
  | {
      resourceKind: 'Segment';
      environment: Environment;
      approvalSettings: ApprovalSettings;
      resource: Segment;
      resourceValidation: (resource: ValidationEntity) => {
        label: ReactNode;
        placeholder: string;
        onValidate: (input: string) => boolean;
      };
    }
  | {
      resourceKind: 'AiConfig';
      impactedEnvironments: List<Environment>;
      approvalSettings: ApprovalSettings;
      environmentSettings: Environment;
      resource: AiConfig;
      resourceValidation: (resource: ValidationEntity) => {
        label: ReactNode;
        placeholder: string;
        onValidate: (input: string) => boolean;
      };
    }
  | {
      resourceKind: 'Experiment';
      environment: Environment;
      approvalSettings: ApprovalSettings;
      resource: ExperimentApprovalResource;
      resourceValidation: (resource: ValidationEntity) => {
        label: ReactNode;
        placeholder: string;
        onValidate: (input: string) => boolean;
      };
    };

export function getApprovalRequestContextPropsForResourceKind({
  resourceKind,
  approvalRequest,
  currentEnvironment,
  projectEnvironments,
  flag,
}: {
  resourceKind: ApprovalRequestResourceKind;
  approvalRequest: ApprovalRequest;
  currentEnvironment: Environment;
  projectEnvironments: List<Environment>;
  flag?: Flag;
}) {
  if (resourceKind === 'Flag') {
    return {
      resourceKind,
      impactedEnvironments:
        approvalRequest.environments?.sortBy(
          (env) => env.name,
          (a, b) => a.localeCompare(b, [], { numeric: true }),
        ) || List<Environment>(),
      approvalSettings: approvalRequest.approvalSettings || createApprovalRequestSettings(),
      environmentSettings: projectEnvironments.reduce(
        (envSettings, env) =>
          envSettings
            .update('requireComments', (requireComments) => requireComments || env.requireComments)
            .update('confirmChanges', (confirmChanges) => confirmChanges || env.confirmChanges),
        createEnvironment(),
      ),
      resource: flag || approvalRequest.resource?.get('flag'),
      resourceValidation: flagValidation,
    } as const;
  }

  const environment = approvalRequest.environments?.get(0) || currentEnvironment;

  if (resourceKind === 'Segment') {
    return {
      resourceKind,
      environment,
      // approvalSettings: approvalRequest.resourceApprovalSettings,
      approvalSettings: approvalRequest.approvalSettings,
      resource: approvalRequest.resource?.get('segment'),
      resourceValidation: segmentValidation,
    } as const;
  }

  if (resourceKind === 'AiConfig') {
    const resource = approvalRequest.resource?.get('aiConfig');
    invariant(resource, 'AI Config resource not found');

    return {
      resourceKind,
      impactedEnvironments:
        approvalRequest.environments?.sortBy(
          (env) => env.name,
          (a, b) => a.localeCompare(b, [], { numeric: true }),
        ) || List<Environment>(),
      approvalSettings: approvalRequest.approvalSettings || createApprovalRequestSettings(),
      environmentSettings: projectEnvironments.reduce(
        (envSettings, env) =>
          envSettings
            .update('requireComments', (requireComments) => requireComments || env.requireComments)
            .update('confirmChanges', (confirmChanges) => confirmChanges || env.confirmChanges),
        createEnvironment(),
      ),
      resource: createAiConfig(resource),
      resourceValidation: aiConfigValiation,
    } as const;
  }

  if (resourceKind === 'Experiment') {
    // At runtime, the experiment field is an Immutable Map from fromJS() rather than an ExperimentApprovalResource.
    // We use .toJS() for key/name but preserve _access as its original Immutable Map so that
    // ExperimentApprovalResource.checkAccess can call .get('denied') on it.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const rawExperiment = approvalRequest.resource?.get('experiment') as any;
    const experimentData = rawExperiment?.toJS?.() ?? rawExperiment;
    const immutableAccess = rawExperiment?.get?.('_access');
    const resource = new ExperimentApprovalResource({
      ...(experimentData ?? { name: '', key: '' }),
      ...(immutableAccess !== undefined ? { _access: immutableAccess } : {}),
    });

    return {
      resourceKind,
      environment,
      approvalSettings:
        approvalRequest.approvalSettings || environment?.approvalSettings || createApprovalRequestSettings(),
      resource,
      resourceValidation: experimentValidation,
    } as const;
  }

  // resourceKind === 'FlagConfiguration'
  return {
    resourceKind,
    resource: flag || approvalRequest.resource?.get('flag'),
    resourceValidation: flagValidation,
    environment,
    approvalSettings:
      approvalRequest.approvalSettings || environment?.approvalSettings || createApprovalRequestSettings(),
  } as const;
}
